/**
 * Helpers for turning raw scheduling/booking API errors into clear, user-facing
 * messages.
 *
 * Background: several backend guards throw `BusinessException`/persistence
 * errors without a user-facing `message`, so the API surfaces an opaque ABP/EF
 * placeholder (e.g. the daily appointment-cap guard — see
 * `docs/implementation/appointment-daily-cap-friendly-error-spec.md`). Until the
 * backend passes a real message, the frontend rescues the known cap error code
 * and replaces opaque placeholders with something readable.
 *
 * Exact-time booking codes (`Routemize.ExactTime.*`) are the same class of
 * problem: ABP returns the unlocalized code as `error.message`. The friendly
 * copy lives on `error.data.Message` (and sometimes `WorkHours`).
 */

const DAILY_CAP_CODE = 'Appointment:UserDailyCapReached';
const BOOKING_LIMIT_CODE = 'Subscription:BookingLimitReached';
const REQUEST_ALREADY_BOOKED_CODE = 'Appointment:RequestAlreadyBooked';

export type SchedulingAlertTone = 'warning' | 'danger';

export interface SchedulingAlert {
  title: string;
  message: string;
  tone: SchedulingAlertTone;
  code?: string;
  /** True when the operator needs to pick a different time before continuing. */
  blocksContinue: boolean;
  hint?: string;
}

interface ExactTimeAlertCopy {
  title: string;
  fallback: string;
  tone: SchedulingAlertTone;
  blocksContinue: boolean;
  hint?: string;
}

const EXACT_TIME_ALERTS: Record<string, ExactTimeAlertCopy> = {
  'Routemize.ExactTime.OutsideWorkHours': {
    title: 'Outside work hours',
    fallback:
      "This time is outside the assigned team member's work hours. Pick a time inside their schedule, or choose one of the recommended slots.",
    tone: 'warning',
    blocksContinue: true,
    hint: 'Recommended slots stay inside this person’s hours.',
  },
  'Routemize.ExactTime.Overlap': {
    title: 'Time already booked',
    fallback: 'This window overlaps an existing appointment. Pick a different time.',
    tone: 'danger',
    blocksContinue: true,
  },
  'Routemize.ExactTime.DriveTimeInfeasible': {
    title: 'Not enough drive time',
    fallback:
      'There isn’t enough time to drive between this stop and a neighboring appointment. Give the route more of a gap, or pick a recommended slot.',
    tone: 'warning',
    blocksContinue: true,
  },
  'Routemize.ExactTime.StartInPast': {
    title: 'Time is in the past',
    fallback: 'The selected time is already in the past. Pick a future start time.',
    tone: 'warning',
    blocksContinue: true,
  },
  'Routemize.ExactTime.InvalidWindow': {
    title: 'Invalid time window',
    fallback: 'The end time needs to be after the start time, with at least 5 minutes for the visit.',
    tone: 'danger',
    blocksContinue: true,
  },
  'Routemize.ExactTime.AssigneeNotAllowed': {
    title: 'Team member not on this form',
    fallback: 'The selected team member is not assigned to this booking form.',
    tone: 'danger',
    blocksContinue: false,
  },
  'Routemize.ExactTime.IncompletePayload': {
    title: 'Exact time is incomplete',
    fallback: 'Start time, end time, and assignee are all required to book an exact time.',
    tone: 'danger',
    blocksContinue: true,
  },
};

// Known placeholder strings the backend returns when an exception is thrown
// without a user-facing message. These must never be shown to end users.
const OPAQUE_BACKEND_MESSAGES = [
  'An error occurred while saving the entity changes. See the inner exception for details.',
];

/** True when `message` is empty or one of the known opaque backend placeholders. */
export function isOpaqueBackendMessage(message?: string | null): boolean {
  if (!message) return true;
  return OPAQUE_BACKEND_MESSAGES.some((m) => message.includes(m));
}

/**
 * True when the request behind this booking was already converted to an appointment
 * (a duplicate/retried submit hit the backend idempotency guard). Callers should treat
 * this as terminal — the booking already exists — rather than "pick another time".
 */
export function isRequestAlreadyBookedError(error: unknown): boolean {
  const { code } = extractApiError(error);
  return code === REQUEST_ALREADY_BOOKED_CODE;
}

// Substrings (lowercased) that mark a raw internal/technical error which should
// never be shown to end users — these leak backend implementation details
// (LINQ/EF/ABP exceptions) instead of explaining what the user should do.
const RAW_TECHNICAL_PATTERNS = [
  'sequence contains no elements',
  'business logic processing failed',
  'object reference not set',
  'an error occurred while saving the entity changes',
  'an error occurred during scheduling',
  'inner exception',
  'stack trace',
];

/**
 * True when a message looks like a raw internal/technical error (a leaked
 * backend exception) rather than a user-facing explanation. Use to suppress
 * such strings in favor of a graceful fallback.
 */
export function isRawTechnicalError(message?: string | null): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return RAW_TECHNICAL_PATTERNS.some((p) => lower.includes(p));
}

/**
 * True when `message` is an unlocalized ABP/BusinessException code
 * (`Routemize.ExactTime.OutsideWorkHours`, `Appointment:UserDailyCapReached`)
 * rather than copy a person should read.
 */
export function isLocalizationKeyMessage(message?: string | null): boolean {
  if (!message) return false;
  return /^[A-Za-z][\w]*([.:][A-Za-z][\w]*)+$/.test(message.trim());
}

function isUsableUserMessage(message?: string | null): message is string {
  return (
    !!message &&
    !isOpaqueBackendMessage(message) &&
    !isRawTechnicalError(message) &&
    !isLocalizationKeyMessage(message)
  );
}

function dailyCapMessage(data: Record<string, unknown> | null | undefined): string {
  const name = (data?.UserDisplayName ?? data?.userDisplayName) as string | undefined;
  const limitRaw = (data?.Limit ?? data?.limit) as number | string | undefined;
  const date = (data?.Date ?? data?.date) as string | undefined;
  const limit = limitRaw != null ? Number(limitRaw) : undefined;

  if (name && limit != null && !Number.isNaN(limit)) {
    const when = date ? ` for ${date}` : ' for this day';
    return `${name} has already reached the maximum of ${limit} appointment${limit === 1 ? '' : 's'}${when}. Move an existing appointment to another day, assign this booking to another team member, or raise the daily limit in the booking form's Settings & Team.`;
  }
  return "This team member has reached the maximum number of appointments allowed for this day. Move an existing appointment to another day, assign the booking to another team member, or raise the daily limit in the booking form's settings.";
}

function readDataString(data: Record<string, unknown> | undefined, ...keys: string[]): string | undefined {
  if (!data) return undefined;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

/** Drop the bypass sentence — this admin flow has no bypass control. */
function sanitizeBackendDataMessage(message: string): string {
  return message
    .replace(/\s*Enable manual bypass to book (?:outside these hours|anyway)\.?/gi, '')
    .trim();
}

function exactTimeMessage(code: string, data: Record<string, unknown> | undefined, rawMessage?: string): string {
  const copy = EXACT_TIME_ALERTS[code];
  const workHours = readDataString(data, 'WorkHours', 'workHours');
  if (code === 'Routemize.ExactTime.OutsideWorkHours' && workHours) {
    return `This team member works ${workHours}. Pick a start and end that fit inside those hours, or choose a recommended slot.`;
  }

  const dataMessage = readDataString(data, 'Message', 'message');
  if (dataMessage && isUsableUserMessage(dataMessage)) {
    return sanitizeBackendDataMessage(dataMessage) || copy.fallback;
  }
  if (rawMessage && isUsableUserMessage(rawMessage)) {
    return sanitizeBackendDataMessage(rawMessage) || copy.fallback;
  }
  return copy.fallback;
}

function extractApiError(error: unknown): { code?: string; rawMessage?: string; data?: Record<string, unknown> } {
  const err = (error ?? {}) as {
    body?: { error?: { code?: string; message?: string; data?: Record<string, unknown> } };
    error?: { code?: string; message?: string; data?: Record<string, unknown> };
    message?: string;
  };
  const apiError = err.body?.error ?? err.error;
  return {
    code: apiError?.code,
    rawMessage: apiError?.message ?? err.message,
    data: apiError?.data,
  };
}

/**
 * Resolve a clear message for a scheduling/booking error.
 *
 * Precedence:
 *  1. Known business codes (daily cap, already booked, booking limit, exact-time)
 *     → friendly copy, using backend `data` when available.
 *  2. A meaningful backend message (not an opaque placeholder, technical dump,
 *     or unlocalized error code).
 *  3. `error.data.Message` when the top-level message was a localization key.
 *  4. The caller-provided `fallback`.
 *
 * Note: callers that handle field-level `validationErrors` should do that
 * before calling this helper; this only covers the general error message tail.
 */
export function resolveSchedulingErrorMessage(error: unknown, fallback: string): string {
  return resolveSchedulingAlert(error, fallback).message;
}

/**
 * Structured alert for booking UI: title, body, warning vs error tone.
 * Use this when rendering an inline callout instead of a raw error string.
 */
export function resolveSchedulingAlert(error: unknown, fallback: string): SchedulingAlert {
  const { code, rawMessage, data } = extractApiError(error);

  if (code === DAILY_CAP_CODE) {
    return {
      title: 'Daily appointment limit reached',
      message: isUsableUserMessage(rawMessage) ? rawMessage : dailyCapMessage(data),
      tone: 'warning',
      code,
      blocksContinue: true,
    };
  }

  if (code === REQUEST_ALREADY_BOOKED_CODE) {
    return {
      title: 'Already scheduled',
      message: isUsableUserMessage(rawMessage)
        ? rawMessage
        : 'This booking has already been scheduled. Refresh to see the existing appointment.',
      tone: 'warning',
      code,
      blocksContinue: false,
    };
  }

  if (code === BOOKING_LIMIT_CODE) {
    const dataMsg = readDataString(data, 'Message', 'message');
    return {
      title: 'Monthly booking limit reached',
      message: dataMsg && isUsableUserMessage(dataMsg)
        ? dataMsg
        : isUsableUserMessage(rawMessage)
          ? rawMessage
          : 'You have reached your monthly booking limit. Please upgrade your plan for more bookings.',
      tone: 'warning',
      code,
      blocksContinue: false,
    };
  }

  if (code && EXACT_TIME_ALERTS[code]) {
    const copy = EXACT_TIME_ALERTS[code];
    return {
      title: copy.title,
      message: exactTimeMessage(code, data, rawMessage),
      tone: copy.tone,
      code,
      blocksContinue: copy.blocksContinue,
      hint: copy.hint,
    };
  }

  const dataMessage = readDataString(data, 'Message', 'message');
  if (isUsableUserMessage(rawMessage)) {
    return {
      title: "Couldn't complete booking",
      message: rawMessage,
      tone: 'danger',
      code,
      blocksContinue: false,
    };
  }
  if (isUsableUserMessage(dataMessage)) {
    return {
      title: "Couldn't complete booking",
      message: sanitizeBackendDataMessage(dataMessage),
      tone: 'danger',
      code,
      blocksContinue: false,
    };
  }

  return {
    title: "Couldn't complete booking",
    message: fallback,
    tone: 'danger',
    code,
    blocksContinue: false,
  };
}

export function isExactTimeConstraintCode(code?: string | null): boolean {
  return !!code && code.startsWith('Routemize.ExactTime.');
}
